import axios from 'axios';
import { authService } from './authService';
import { errorLogService } from './errorLogService';
import { triggerUpgrade } from './upgradeBus';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Criar instância do Axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Request capture for health check debugging ---
let _capturedRequests = [];
let _isCapturing = false;

export const startCapturing = () => { _isCapturing = true; _capturedRequests = []; };
export const stopCapturing = () => { _isCapturing = false; return [..._capturedRequests]; };

// Interceptor de request - adiciona token JWT apenas se válido
api.interceptors.request.use(
  (config) => {
    if (_isCapturing) {
      let body;
      try { body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data; } catch { body = config.data; }
      _capturedRequests.push({ method: config.method?.toUpperCase(), url: config.url, body });
    }

    const token = authService.getToken();
    
    // Validar se o token existe e não está expirado
    if (token && authService.isTokenValid()) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (token) {
      // Token existe mas está expirado - redirecionar para login
      console.warn('[API] Token expirado, redirecionando para login...');
      const currentPath = window.location.pathname + window.location.search;
      authService.removeToken();
      authService.redirectToLogin(currentPath);
      // Abortar requisição
      return Promise.reject(new Error('Token expirado'));
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de response - trata erros 401 e loga erros HTTP
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Ida ao Login não é erro: 401 de requisição que saiu SEM credencial (visitante ainda não logado)
    // e a requisição que o próprio interceptor de request abortou por token vencido. Ambas terminam
    // em redirectToLogin; logá-las enchia o painel de "HTTP 401"/"Token expirado" (varredura 28/09).
    const semCredencial = !error.config?.headers?.Authorization;
    const idaAoLogin = (error.response?.status === 401 && semCredencial)
      || (!error.response && error.message === 'Token expirado');

    // NÃO logar erros para /console/errors (evita loop infinito)
    if (!idaAoLogin && !error.config?.url?.includes('/console/errors')) {
      const errorMsg = error.message || 'Network error';
      const statusText = error.response?.statusText ? ` (${error.response.statusText})` : '';
      const fullError = `HTTP ${error.response?.status || 'Error'}${statusText}: ${errorMsg}`;

      // Extrair contexto detalhado da requisição para facilitar diagnóstico
      const requestData = error.config?.data;
      let requestPayload;
      try {
        requestPayload = typeof requestData === 'string' ? JSON.parse(requestData) : requestData;
      } catch {
        requestPayload = requestData;
      }
      const apiContext = {
        method: error.config?.method?.toUpperCase(),
        requestUrl: error.config?.url,
        requestPayload,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
      };

      errorLogService.logError(fullError, error.config?.url, apiContext).catch(() => {});
    }

    if (error.response?.status === 401) {
      // Token inválido ou expirado - salva path atual para retornar após login
      const currentPath = window.location.pathname + window.location.search;
      authService.redirectToLogin(currentPath);
    }

    // 402 Payment Required = recusa por plano. Abre o UpgradeSheet com o payload
    // estruturado do backend (rede de segurança onde o gating proativo não cobriu).
    if (error.response?.status === 402) {
      triggerUpgrade(error.response?.data || {});
    }
    return Promise.reject(error);
  }
);

export default api;
